import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bookmark, Plus, X } from "lucide-react";
import { Button, Input } from "./UIKit";
import { listPresets, createPreset, deletePreset } from "../../api/filterPresets";
import ConfirmModal from "./ConfirmModal";

/**
 * Saved filter presets UI.
 *
 * Modes:
 *  - "list": shows the list of saved presets. Clicking a chip calls `onApply`
 *    with that preset's filters. The parent decides what to do next
 *    (e.g. set form values + navigate).
 *  - "save": shows only the "Save current filters" affordance. Used on the
 *    results page so the agent saves whatever filter set produced the view.
 */
export default function PresetBar({ currentFilters, onApply, mode = "list" }) {
  const showList = mode === "list";
  const showSaveAction = mode === "save";

  const [presets, setPresets] = useState([]);
  const [showSave, setShowSave] = useState(false);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name }

  const refresh = () => {
    if (!showList) return;
    listPresets().then(setPresets).catch(() => {});
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const cleanFilters = () => {
    const out = {};
    Object.entries(currentFilters || {}).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) out[k] = v;
    });
    return out;
  };

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) return toast.error("Preset name required");
    setLoading(true);
    try {
      await createPreset({ name: trimmed, filters: cleanFilters() });
      toast.success(`Saved "${trimmed}"`);
      setName(""); setShowSave(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setLoading(false); }
  };

  const remove = async (id, presetName) => {
    setDeleteTarget({ id, name: presetName });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deletePreset(deleteTarget.id);
      toast.success("Preset deleted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <>
      <div className="border-b border-zinc-200 bg-white px-6 py-3" data-testid="preset-bar">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-zinc-600">
          <Bookmark className="h-3.5 w-3.5" /> Presets
        </div>

        {showList && presets.length === 0 && (
          <span className="text-xs text-zinc-400" data-testid="no-presets-message">
            No saved presets yet.
          </span>
        )}

        {showList && (
          <div className="flex items-center gap-1.5 flex-wrap">
            {presets.map((p) => (
              <div
                key={p.id}
                className="group inline-flex items-center gap-0.5 rounded-full border border-zinc-200 bg-zinc-50 hover:border-zinc-950 hover:bg-white transition-colors"
                data-testid={`preset-chip-${p.id}`}
              >
                <button
                  data-testid={`preset-apply-${p.id}`}
                  onClick={() => onApply && onApply(p.filters || {}, p)}
                  className="pl-3 pr-2 py-1 text-xs font-semibold text-zinc-950"
                >
                  {p.name}
                </button>
                <button
                  data-testid={`preset-delete-${p.id}`}
                  onClick={() => remove(p.id, p.name)}
                  className="pr-2 py-1 text-zinc-400 hover:text-red-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {showSaveAction && (
          <div className="ml-auto flex items-center gap-2">
            {showSave ? (
              <>
                <Input
                  data-testid="preset-name-input"
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && save()}
                  placeholder="e.g. Hot Miami FSBO"
                  className="h-8 w-56 text-xs"
                />
                <Button data-testid="preset-save-button" variant="primary" onClick={save} disabled={loading} className="h-8 px-3 text-xs">
                  Save
                </Button>
                <button
                  data-testid="preset-cancel-button"
                  onClick={() => { setShowSave(false); setName(""); }}
                  className="text-xs text-zinc-500 hover:text-zinc-950"
                >
                  Cancel
                </button>
              </>
            ) : (
              <Button
                data-testid="preset-save-new-button"
                variant="outline"
                onClick={() => setShowSave(true)}
                className="h-8 px-3 text-xs"
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Save current filters
              </Button>
            )}
          </div>
        )}
      </div>
      </div>

      {deleteTarget && (
        <ConfirmModal
          title="Delete preset?"
          message={<>Are you sure you want to delete <span className="font-semibold text-zinc-900">"{deleteTarget.name}"</span>? This cannot be undone.</>}
          confirmLabel="Delete"
          confirmVariant="danger"
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </>
  );
}
